import { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, ArrowLeft } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * Pagina 404: astronauta a la deriva atravesando todo el viewport.
 * Mensaje centrado, astronauta animado diagonal, fondo dark con estrellas.
 * Usa colores de marca (primary/cyan) — sin luna.
 */
const NotFoundPage = () => {
    const navigate = useNavigate();
    // Usa el hook para que el cleanup restaure el DEFAULT_TITLE al salir del 404.
    useDocumentTitle('404 — Perdido en el espacio');

    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prevOverflow; };
    }, []);

    const estrellas = useMemo(() => {
        const arr = [];
        for (let i = 0; i < 120; i++) {
            arr.push({
                id: i,
                top: Math.random() * 100,
                left: Math.random() * 100,
                size: 1 + Math.random() * 2.2,
                delay: Math.random() * 5,
                duration: 2 + Math.random() * 4
            });
        }
        return arr;
    }, []);

    return (
        <div className="notfound-scene">
            {/* Estrellas de fondo */}
            <div className="notfound-stars" aria-hidden="true">
                {estrellas.map(s => (
                    <span
                        key={s.id}
                        className="notfound-star"
                        style={{
                            top: `${s.top}%`,
                            left: `${s.left}%`,
                            width: `${s.size}px`,
                            height: `${s.size}px`,
                            animationDelay: `${s.delay}s`,
                            animationDuration: `${s.duration}s`
                        }}
                    />
                ))}
            </div>

            {/* Nebulosas de color de marca */}
            <div className="notfound-nebula notfound-nebula-primary" aria-hidden="true" />
            <div className="notfound-nebula notfound-nebula-cyan" aria-hidden="true" />

            {/* Astronauta que atraviesa el viewport en diagonal (loop) */}
            <div className="notfound-astro-wrap" aria-hidden="true">
                <svg
                    className="notfound-astro"
                    viewBox="0 0 200 240"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        <linearGradient id="visorGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="rgb(var(--primary))" stopOpacity="0.75" />
                            <stop offset="100%" stopColor="rgb(var(--cyan2, 34 211 238))" stopOpacity="0.9" />
                        </linearGradient>
                    </defs>

                    {/* Cable/manguera que arrastra detras */}
                    <path
                        d="M 60 170 Q 30 200 5 235"
                        stroke="#e8ebef"
                        strokeWidth="3"
                        strokeLinecap="round"
                        fill="none"
                        opacity="0.7"
                    />

                    {/* Mochila */}
                    <rect x="55" y="90" width="90" height="80" rx="12" fill="#c9ced4" stroke="#8b9199" strokeWidth="1.5" />
                    <rect x="66" y="102" width="14" height="12" rx="2" fill="rgb(var(--primary))" opacity="0.85" />
                    <rect x="120" y="102" width="14" height="12" rx="2" fill="rgb(var(--cyan2, 34 211 238))" opacity="0.85" />
                    <rect x="66" y="122" width="68" height="4" rx="1" fill="#7b8188" opacity="0.6" />
                    <rect x="66" y="132" width="68" height="4" rx="1" fill="#7b8188" opacity="0.6" />

                    {/* Cuerpo (traje blanco) */}
                    <rect x="65" y="95" width="70" height="85" rx="18" fill="#ffffff" stroke="#c9ced4" strokeWidth="1.5" />
                    <rect x="65" y="150" width="70" height="10" fill="#e5e8ec" />
                    <rect x="95" y="150" width="10" height="10" fill="rgb(var(--primary))" />

                    {/* Brazos */}
                    <path d="M 65 105 Q 40 85 30 60" stroke="#ffffff" strokeWidth="18" strokeLinecap="round" fill="none" />
                    <path d="M 135 105 Q 160 85 170 60" stroke="#ffffff" strokeWidth="18" strokeLinecap="round" fill="none" />
                    {/* Guantes en color de marca */}
                    <circle cx="28" cy="58" r="12" fill="rgb(var(--primary))" />
                    <circle cx="172" cy="58" r="12" fill="rgb(var(--primary))" />

                    {/* Piernas */}
                    <rect x="72" y="170" width="22" height="55" rx="10" fill="#ffffff" stroke="#c9ced4" strokeWidth="1.5" transform="rotate(-8 83 197)" />
                    <rect x="106" y="170" width="22" height="55" rx="10" fill="#ffffff" stroke="#c9ced4" strokeWidth="1.5" transform="rotate(8 117 197)" />
                    {/* Botas en color de marca */}
                    <ellipse cx="76" cy="228" rx="16" ry="8" fill="rgb(var(--primary))" />
                    <ellipse cx="124" cy="228" rx="16" ry="8" fill="rgb(var(--primary))" />

                    {/* Casco */}
                    <circle cx="100" cy="60" r="42" fill="#ffffff" stroke="#c9ced4" strokeWidth="2" />
                    {/* Visor con gradient de marca */}
                    <ellipse cx="100" cy="60" rx="32" ry="28" fill="#0a0f1a" />
                    <ellipse cx="100" cy="60" rx="32" ry="28" fill="url(#visorGrad)" opacity="0.55" />
                    <ellipse cx="88" cy="50" rx="10" ry="7" fill="#ffffff" opacity="0.4" transform="rotate(-30 88 50)" />
                    <circle cx="112" cy="68" r="3" fill="#ffffff" opacity="0.3" />

                    {/* Antena con luz roja parpadeante */}
                    <line x1="100" y1="18" x2="100" y2="10" stroke="#c9ced4" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="100" cy="8" r="3" fill="rgb(var(--danger))">
                        <animate attributeName="opacity" values="1;0.3;1" dur="1.6s" repeatCount="indefinite" />
                    </circle>
                </svg>
            </div>

            {/* Contenido centrado en el viewport */}
            <div className="notfound-content">
                <div className="notfound-code">404</div>
                <h1 className="notfound-title">Hmmm...</h1>
                <p className="notfound-subtitle">
                    Te alejaste tanto que perdimos tu señal.
                    <br />
                    Esta página no existe en nuestro sistema solar.
                </p>
                <div className="notfound-actions">
                    <button
                        type="button"
                        className="notfound-btn notfound-btn-primary"
                        onClick={() => navigate('/')}
                    >
                        <Home size={14} aria-hidden="true" />
                        Volver al inicio
                    </button>
                    <button
                        type="button"
                        className="notfound-btn notfound-btn-ghost"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={14} aria-hidden="true" />
                        Regresar
                    </button>
                </div>
                <p className="notfound-help">
                    ¿Crees que es un error? <Link to="/dashboard">Ir al dashboard</Link>
                </p>
            </div>
        </div>
    );
};

export default NotFoundPage;
